import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from '@azure/msal-react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { useBrandingStore } from './stores/branding'
import { useAuthStore } from './stores/auth'
import { useCurrentUser } from './hooks/useCurrentUser'
import { api } from './lib/api'
import { getImpersonationSession } from './lib/impersonation'
import AppShell from './components/layout/AppShell'
import LoginPage from './pages/Login'
import SOWPublicView from './pages/SOWPublicView'
import NoInviteFoundPage from './pages/NoInviteFound'
import AccessDeniedPage from './pages/AccessDenied'
import AccessBlockedPage from './pages/AccessBlocked'
import ProvisionTenantPage from './pages/ProvisionTenant'
import AdminConsole from './pages/admin/AdminConsole'
import ImpersonationEntry from './pages/ImpersonationEntry'

function AppErrorFallback() {
  return (
    <div className="flex h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md space-y-3 text-center">
        <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
        <p className="text-sm text-muted-foreground">
          An unexpected error occurred. Refresh the page to continue.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Refresh
        </button>
      </div>
    </div>
  )
}

// Allowlist, not a denylist: AppShell renders for exactly one authStatus
// value, checked explicitly below. Every other value -- the five named
// rejection reasons ('not_provisioned' included -- it gets its own page,
// not the catch-all, because it's the one state a signed-in user can
// resolve themselves), 'blocked' (any other rejection reason, known or
// not), 'loading', or a future AuthStatus this function hasn't been updated
// for -- falls through to the catch-all AccessBlockedPage at the end. It must be
// structurally impossible for an unrecognized state to reach AppShell; the
// old version of this function checked for four known-bad strings and
// rendered AppShell for anything else, which is exactly how a 403
// (UNKNOWN_TENANT, 2026-09) reached the authenticated dashboard shell for a
// tenant with no registry row. See quill-bug-log.md.
export function AuthenticatedApp() {
  const { authStatus } = useAuthStore()
  // useCurrentUser() must still be called to drive the /auth/me query and
  // its store-syncing effect -- but its OWN `isLoading` is deliberately not
  // read here. That value updates synchronously with the query object on
  // this render; authStatus updates one render later, from that query's
  // effect. Branching on both in the same render opened a one-render
  // window where isLoading had already flipped to false but authStatus
  // hadn't caught up from 'loading' yet -- which fell through every branch
  // below to the AccessBlockedPage catch-all, transiently mounting it.
  // authStatus is the only source of truth this function reads from now,
  // so "still loading" and "which page" can never disagree.
  //
  // `refetch` is threaded down to AccessBlockedPage as a prop rather than
  // AccessBlockedPage calling useCurrentUser() itself for the same reason:
  // a second observer on the same query key mounts fresh every time
  // AccessBlockedPage does, and with the default staleTime of 0 a fresh
  // mount always refetches -- which re-runs that second observer's own
  // copy of the store-syncing effect, producing a genuine infinite refetch
  // loop independent of the race above. One call site owns this query;
  // every consumer of its result is a prop, never a second subscription.
  const { refetch } = useCurrentUser()

  useEffect(() => {
    const id = setInterval(() => { api.get('/health').catch(() => {}) }, 4 * 60 * 1000)
    return () => clearInterval(id)
  }, [])

  if (authStatus === 'loading') {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (authStatus === 'authenticated') return <AppShell />

  if (authStatus === 'pending' || authStatus === 'no_invite') return <NoInviteFoundPage />
  if (authStatus === 'denied') return <AccessDeniedPage />
  if (authStatus === 'not_provisioned') return <ProvisionTenantPage onProvisioned={() => void refetch()} />

  // External tenant — polite rejection (MSAL is still authenticated; we gate at API level)
  if (authStatus === 'external') {
    return (
      <div className="flex h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md space-y-3 text-center">
          <h1 className="text-xl font-semibold text-foreground">Not Authorized</h1>
          <p className="text-sm text-muted-foreground">
            Your Microsoft account is not authorized for this application.
          </p>
        </div>
      </div>
    )
  }

  // authStatus === 'blocked', or any value not explicitly matched above.
  return <AccessBlockedPage onRetry={() => void refetch()} />
}

export default function App() {
  const { applyToDocument } = useBrandingStore()

  useEffect(() => {
    applyToDocument()
  }, [applyToDocument])

  // Read once per render, not subscribed reactively: every transition into
  // or out of an impersonation session goes through a full browser
  // navigation (ImpersonationEntry.tsx on entry, ImpersonationBanner.tsx's
  // "End impersonation" and api.ts's own 401 handling on exit) specifically
  // so App() re-mounts fresh and re-reads this, rather than needing a
  // storage-event listener to catch a change made in the same tab.
  const isImpersonating = getImpersonationSession() !== null

  return (
    <BrowserRouter>
      <Routes>
        {/* Unauthenticated public SOW view */}
        <Route path="/sow/:shareToken" element={
          <ErrorBoundary FallbackComponent={AppErrorFallback}>
            <SOWPublicView />
          </ErrorBoundary>
        } />

        {/* Pool-scoped admin console -- its own, separate Entra app
            registration/MSAL instance (lib/adminMsal.ts), never the
            tenant-facing AuthenticatedTemplate/msalInstance below. Not
            linked from tenant navigation, but that is a discoverability
            choice, not the security boundary -- withAdminAuth.ts on the
            API side is what actually gates this; see that file and
            AdminConsole.tsx's own header comments. */}
        <Route path="/admin/*" element={
          <ErrorBoundary FallbackComponent={AppErrorFallback}>
            <AdminConsole />
          </ErrorBoundary>
        } />

        {/* Landing page for a freshly-opened impersonation tab (see
            lib/impersonation.ts) -- deliberately a sibling of /admin/* and
            /sow/:shareToken, not nested under the /* route below: a fresh
            tab has no MSAL account yet, so if this were inside
            AuthenticatedTemplate/UnauthenticatedTemplate it would render
            LoginPage instead of ever getting a chance to store the
            session. */}
        <Route path="/impersonate" element={
          <ErrorBoundary FallbackComponent={AppErrorFallback}>
            <ImpersonationEntry />
          </ErrorBoundary>
        } />

        {/* All other routes require auth -- either a real MSAL session, or
            an active impersonation session (checked first: an
            impersonating admin has no real MSAL account in this tab, so
            AuthenticatedTemplate's own account check would otherwise
            render LoginPage underneath a session that's actually valid). */}
        <Route
          path="/*"
          element={
            isImpersonating ? (
              <ErrorBoundary FallbackComponent={AppErrorFallback}>
                <AuthenticatedApp />
              </ErrorBoundary>
            ) : (
              <>
                <AuthenticatedTemplate>
                  <ErrorBoundary FallbackComponent={AppErrorFallback}>
                    <AuthenticatedApp />
                  </ErrorBoundary>
                </AuthenticatedTemplate>
                <UnauthenticatedTemplate>
                  <LoginPage />
                </UnauthenticatedTemplate>
              </>
            )
          }
        />
      </Routes>
    </BrowserRouter>
  )
}
